import { useEffect, useState, useMemo, memo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Col,
    Container,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    NavbarBrand,
    Offcanvas,
    OffcanvasBody,
    OffcanvasHeader,
    Row
} from 'reactstrap';
import { GetMemberInfoAPI, LanguagesJsonAPI } from '../../helpers/APIS/CommonApisSchema';
import AssessmentsListPopup from '../../pages/Assessments/AssessmentsListPopup';
import { DataKey } from '../../Store/callAPI/allAPIs';
import { SelectFullStateOfThisAPI } from '../../Store/callAPI/selectors';
import { updateSelectedLanguageToStore } from '../../Store/commonStore/actions';
import { SelectSearchValInStore } from '../../Store/commonStore/selectors';
import { getSpecificTenantLogo } from '../../utils/Utils';
import { headerMenuItems } from '../constants/CommonConstants';
import { handleLogout } from './commonUtils';
import HelpAndSupport from './HelpAndSupport';

const StaticNavbar = () => {
    const history = useNavigate();
    const [isProfileDropdown, setIsProfileDropdown] = useState(false);
    const [isMenuDropdown, setIsMenuDropdown] = useState(false);
    const [showAssessmentPopUp, setShowAssessmentPopUp] = useState(false);
    const [showCanvas, setShowCanvas] = useState(false);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const [isLanguageDropdown, setIsLanguageDropdown] = useState(false);

    const linkParams = useSelector((state) => SelectSearchValInStore(state, 'linkParams'));
    const selectedLanguage = useSelector((state) => SelectSearchValInStore(state, 'selectedLanguage'));
    const GetMemberInfoAPIData = useSelector((state) => SelectFullStateOfThisAPI(state, GetMemberInfoAPI));
    const LanguagesJsonAPIData = useSelector((state) => SelectFullStateOfThisAPI(state, LanguagesJsonAPI));

    const languagesList = useMemo(() => LanguagesJsonAPIData?.[DataKey]?.data || [], [LanguagesJsonAPIData]);

    let memberData = GetMemberInfoAPIData?.[DataKey]?.data || { fullName: '' };
    const assessments = GetMemberInfoAPIData?.response?.data?.assessments || [];

    // Initialize selectedLanguage if not set
    useEffect(() => {
        if (!selectedLanguage && languagesList?.length > 0) {
            const language = languagesList?.find((obj) => obj?.value === linkParams?.lang?.toUpperCase());
            if (language) {
                updateSelectedLanguageToStore(language);
            }
        }
    }, [selectedLanguage, languagesList, linkParams]);

    const toggleProfileDropdown = () => {
        setIsProfileDropdown(!isProfileDropdown);
    };

    const toggleMenuDropdown = () => {
        setIsMenuDropdown(!isMenuDropdown);
    };

    const toggleHelpModal = () => {
        setIsHelpModalOpen(!isHelpModalOpen);
    };

    const toggleLanguageDropdown = () => {
        setIsLanguageDropdown(!isLanguageDropdown);
    };

    const getUserInitials = (name) => {
        if (!name) return '';
        return name
            .split(' ')
            .map((word) => word[0])
            .join('')
            .substring(0, 2)
            .toUpperCase();
    };

    const handleMenuItemClick = (memnuItem) => {
        if (memnuItem.id === 1) {
            assessments?.length === 1
                ? history('/assessments', { state: { assessmentData: assessments[0] } })
                : setShowAssessmentPopUp(true);
        } else {
            history(memnuItem.link);
        }
    };

    const LinksList = headerMenuItems(linkParams?.tenant);

    return (
        <Container fluid className="bg-white mb-2 pe-0 max-width-1920 py-3">
            <Row className="w-100 align-items-center">
                <Col className="d-flex justify-content-between align-items-center">
                    <NavbarBrand
                        id="navbar-brand-logo"
                        title="Go to Care Plan"
                        className="cursor-pointer"
                        onClick={() => history('/careplan')}
                    >
                        {getSpecificTenantLogo(linkParams?.tenant)}
                    </NavbarBrand>
                    <div className="d-flex align-items-center ">
                        {/* Menu Icon Dropdown */}
                        {LinksList?.length > 1 && (
                            <Dropdown isOpen={isMenuDropdown} toggle={toggleMenuDropdown} id="menuDropdown" className=" me-3">
                                <DropdownToggle className="btn btn-soft-primary btn-icon btn-sm sm-btn" tag="button">
                                    <i className="ri-menu-line fs-4 p-2"></i>
                                </DropdownToggle>

                                <DropdownMenu>
                                    {LinksList?.map((item, idx) => (
                                        <DropdownItem
                                            key={item?.id || item.label || idx}
                                            onClick={() => handleMenuItemClick(item)}
                                            className="py-2"
                                        >
                                            {item.label}
                                        </DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </Dropdown>
                        )}
                        {/* Help Icon */}
                        <div id="help-icon-button" title="Help and Support" className="cursor-pointer me-3" onClick={toggleHelpModal}>
                            <i className="fa-regular fa-circle-question fs-4 text-secondary"></i>
                        </div>

                        {/* Language Icon */}
                        <Dropdown
                            id="language-dropdown-toggle"
                            title="Select Language"
                            isOpen={isLanguageDropdown}
                            toggle={toggleLanguageDropdown}
                            className="me-3"
                        >
                            <DropdownToggle tag="span" className="cursor-pointer">
                                <i className="fa-solid fa-language fs-4 text-secondary"></i>
                            </DropdownToggle>
                            <DropdownMenu>
                                {languagesList?.map((lang) => (
                                    <DropdownItem
                                        key={lang.value}
                                        onClick={() => updateSelectedLanguageToStore(lang)}
                                        active={selectedLanguage?.value === lang.value}
                                        className={`p-3 px-3 fw-bold ${
                                            selectedLanguage?.value === lang.value ? 'bg-soft-primary text-white' : ''
                                        }`}
                                    >
                                        {lang.label}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </Dropdown>

                        <Dropdown
                            id="profile-dropdown-toggle"
                            title="User Profile and Logout"
                            isOpen={isProfileDropdown}
                            toggle={toggleProfileDropdown}
                            className="p-2 bg-primary rounded-circle"
                        >
                            <DropdownToggle className=" border-0 d-flex align-items-center bg-primary rounded-circle p-0 ms-0" tag="button">
                                <span className="rounded-circle d-flex justify-content-center align-items-center text-white fw-semibold text-uppercase avatar-bg-primary">
                                    {getUserInitials(memberData?.fullName)}
                                </span>
                            </DropdownToggle>
                            <DropdownMenu className="dropdown-menu-end">
                                <h6 className="dropdown-header">Welcome {memberData?.fullName}</h6>
                                <DropdownItem
                                    id="logout-button"
                                    title="Log out"
                                    onClick={() => handleLogout(linkParams, history)}
                                    className="py-2"
                                >
                                    <i className="ri-logout-box-r-line text-muted fs-16 align-middle me-1"></i>
                                    <span className="align-middle" data-key="t-logout">
                                        Logout
                                    </span>
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    </div>

                    <Offcanvas isOpen={showCanvas} toggle={() => setShowCanvas(false)} direction="end" className="offcanvas-menu w-50 p-0">
                        <OffcanvasHeader
                            toggle={() => setShowCanvas(false)}
                            className="d-flex justify-content-between align-items-center p-2"
                        >
                            <h5 className="mb-0 d-flex align-items-center">
                                <span className="rounded-circle bg-primary d-flex justify-content-center align-items-center me-2 text-white  text-uppercase avatar-profile">
                                    {getUserInitials(memberData?.fullName)}
                                </span>
                                <span className=" text-dark">
                                    {memberData?.fullName
                                        ? memberData.fullName
                                              .split(' ')
                                              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                              .join(' ')
                                        : ''}
                                </span>
                            </h5>
                        </OffcanvasHeader>

                        <OffcanvasBody className="p-0">
                            <ul className="list-unstyled static-navbar m-0 p-1">
                                <li className="py-2 ">
                                    <button className="btn w-100 text-start" onClick={() => history('/assessments')}>
                                        Assessments
                                    </button>
                                </li>
                                <li className="py-2">
                                    <button className="btn  w-100 text-start" onClick={() => history('/careplan')}>
                                        Care Plan
                                    </button>
                                </li>
                            </ul>
                        </OffcanvasBody>
                        <Button
                            id="offcanvas-logout-button"
                            title="Log out"
                            onClick={() => handleLogout(linkParams, history)}
                            className="w-100 bg-light border-0 text-dark d-flex align-items-center justify-content-center"
                        >
                            <i className="ri-logout-box-r-line text-dark fs-16 align-middle me-2"></i>
                            <span className="align-middle" data-key="t-logout">
                                Logout
                            </span>
                        </Button>
                    </Offcanvas>
                </Col>
            </Row>
            <AssessmentsListPopup show={showAssessmentPopUp} onCancel={() => setShowAssessmentPopUp(false)} />
            <HelpAndSupport isOpen={isHelpModalOpen} toggle={toggleHelpModal} />
        </Container>
    );
};

export default memo(StaticNavbar);
